import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import splashIcon from "../assets/images/splash-icon.png";
import {
	ArrowRight as FaArrowRight,
	CircleCheck as FaCheckCircle,
	TrendingUp as FaChartLine,
	TriangleAlert as FaExclamationTriangle,
} from "lucide-react";
import { getBusinessDetailsApi } from "../api/business.api";
import { useTheme } from "../context/ThemeContext";
import Button from "../components/ui/Button";
import type { ApiError } from "../types/api";
import type { RawBusiness } from "../types/business";

interface PulseDetails {
	averageRating?: number;
	avgRating?: number;
	totalReviews?: number;
	reviewCount?: number;
	responseRate?: number;
	reviewsNeedingResponse?: number;
	unansweredReviews?: number;
}

interface BuildPulseStatsArgs {
	business?: RawBusiness | null;
	details?: PulseDetails;
	calculateScore: (averageRating: number, responseRate: number, totalReviews: number) => number;
}

export const buildPulseStats = ({ business, details = {}, calculateScore }: BuildPulseStatsArgs) => {
	const averageRating =
		details?.averageRating ??
		details?.avgRating ??
		business?.avgRating ??
		business?.averageRating ??
		0;
	const totalReviews =
		details?.totalReviews ??
		details?.reviewCount ??
		business?.totalReviews ??
		business?.totalRatings ??
		0;
	const responseRate = details?.responseRate ?? 0;
	const unansweredReviews =
		details?.reviewsNeedingResponse ?? details?.unansweredReviews ?? 0;

	return {
		score: calculateScore(averageRating, responseRate, totalReviews),
		totalReviews,
		unansweredReviews,
		averageRating: parseFloat(String(averageRating)) || 0,
	};
};

export default function PulseCheck() {
	const navigate = useNavigate();
	const location = useLocation();
	const { theme, mode } = useTheme();
	// Neutral border for structure — theme.border carries a blue tint meant for
	// accent surfaces; this page uses it only where blue is intentional.
	const subtleBorder = mode === "dark" ? theme.border : "#E7EAF0";

	const initialBusiness =
		location.state?.business ||
		JSON.parse(localStorage.getItem("selected_business") || "null");

	const businessId =
		location.state?.businessId ||
		initialBusiness?.id ||
		initialBusiness?.businessId ||
		initialBusiness?.placeId;

	const [businessData, setBusinessData] = useState(initialBusiness);
	const [displayScore, setDisplayScore] = useState(0);

	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");

	const isGoogleNotConnected =
		localStorage.getItem("google_connect_skipped") === "true" &&
		localStorage.getItem("google_connected") !== "true";

	const [stats, setStats] = useState({
		score: 0,
		totalReviews: 0,
		unansweredReviews: 0,
		averageRating: 0,
	});

	// Calculate reputation score from available data
	const calculateScore = (avgRating: number, responseRate: number, totalReviews: number) => {
		const ratingScore = (parseFloat(String(avgRating)) / 5) * 40; // Max 40 points
		const replyScore = responseRate * 0.4; // Max 40 points (responseRate is 0-100)
		const volumeScore = Math.min(totalReviews, 100) / 5; // Max 20 points

		return Math.round(ratingScore + replyScore + volumeScore);
	};

	useEffect(() => {
		const fetchPulseData = async () => {
			if (isGoogleNotConnected) {
				setIsLoading(false);
				return;
			}

			if (!businessId) {
				setError(
					"No business selected. Please search and connect a business first.",
				);
				setIsLoading(false);
				return;
			}

			try {
				setIsLoading(true);
				setError("");

				const response = await getBusinessDetailsApi(businessId);
				const payload = response?.data || response || {};
				const details = payload?.data || payload;
				const business = details?.business || initialBusiness || null;

				setBusinessData(business);

				setStats(
					buildPulseStats({ business, details, calculateScore }),
				);
			} catch (err) {
				setBusinessData(initialBusiness);

				setStats(
					buildPulseStats({
						business: initialBusiness,
						details: {},
						calculateScore,
					}),
				);

				if (!initialBusiness) {
					setError(
						(err as ApiError)?.response?.data?.message || "Unable to load reputation pulse.",
					);
				}
			} finally {
				setIsLoading(false);
			}
		};

		fetchPulseData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [businessId, isGoogleNotConnected]);

	useEffect(() => {
		let start = 0;
		const duration = 1600;
		const increment = Math.max(1, Math.ceil(stats.score / (duration / 20)));

		const timer = setInterval(() => {
			start += increment;

			if (start >= stats.score) {
				start = stats.score;
				clearInterval(timer);
			}

			setDisplayScore(start);
		}, 20);

		return () => clearInterval(timer);
	}, [stats.score]);

	const { scoreColor, scoreLabel } = useMemo(() => {
		if (stats.score >= 80) {
			return { scoreColor: theme.success, scoreLabel: "Good" };
		}

		if (stats.score >= 50) {
			return { scoreColor: theme.warning, scoreLabel: "Room for Improvement" };
		}

		return { scoreColor: theme.danger, scoreLabel: "Needs Attention" };
	}, [stats.score, theme]);

	const radius = 78;
	const stroke = 12;
	const normalizedRadius = radius - stroke / 2;
	const circumference = normalizedRadius * 2 * Math.PI;
	const progress = (displayScore / 100) * circumference;
	const strokeDashoffset = circumference - progress;

	const StatCard = ({ icon, label, value, iconColor }: { icon: ReactNode; label: string; value: ReactNode; iconColor: string }) => (
		<div className="rounded-2xl border p-4" style={{ borderColor: subtleBorder, background: theme.card }}>
			<div className="flex items-center gap-3">
				<div
					className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl"
					style={{ backgroundColor: `${iconColor}1F`, color: iconColor }}
				>
					{icon}
				</div>

				<div className="min-w-0">
					<p className="text-sm font-medium" style={{ color: theme.textMuted }}>{label}</p>
					<p className="mt-0.5 text-lg font-semibold" style={{ color: theme.text }}>{value}</p>
				</div>
			</div>
		</div>
	);

	if (isLoading) {
		return (
			<div className="flex min-h-screen items-center justify-center px-4" style={{ background: theme.background }}>
				<div className="text-center">
					<span className="mx-auto block h-10 w-10 animate-spin rounded-full border-2" style={{ borderColor: "rgba(2,34,241,0.3)", borderTopColor: theme.primary }} />
					<p className="mt-4 text-sm font-medium" style={{ color: theme.textMuted }}>
						Analyzing your business reputation...
					</p>
				</div>
			</div>
		);
	}

	if (isGoogleNotConnected) {
		return (
			<div className="flex min-h-screen items-center justify-center px-4" style={{ background: theme.background }}>
				<div className="w-full max-w-md rounded-2xl border p-6 text-center" style={{ borderColor: subtleBorder, background: theme.card }}>
					<FaExclamationTriangle className="mx-auto" size={28} color={theme.warning} />

					<h1 className="mt-4 text-lg font-semibold" style={{ color: theme.text }}>
						Connect Your Business to See Your Pulse Check
					</h1>

					<p className="mt-2 text-sm leading-6" style={{ color: theme.textMuted }}>
						You skipped connecting your Google Business Profile, so we can't
						show your real reputation score yet. Connect it to see accurate
						reviews, ratings, and reply data.
					</p>

					<Button variant="primary" theme={theme} onClick={() => navigate("/connect-google")} className="mt-6 w-full py-3">
						Connect Google Business Profile
					</Button>

					<Button variant="secondary" theme={theme} onClick={() => navigate("/dashboard/overview")} className="mt-3 w-full py-3">
						Go to Dashboard Instead
					</Button>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex min-h-screen items-center justify-center px-4" style={{ background: theme.background }}>
				<div className="w-full max-w-md rounded-2xl border p-6 text-center" style={{ borderColor: subtleBorder, background: theme.card }}>
					<FaExclamationTriangle className="mx-auto" size={28} color={theme.danger} />

					<h1 className="mt-4 text-lg font-semibold" style={{ color: theme.text }}>
						Unable to Load Pulse Check
					</h1>

					<p className="mt-2 text-sm leading-6" style={{ color: theme.textMuted }}>{error}</p>

					<Button variant="primary" theme={theme} onClick={() => navigate("/search-business")} className="mt-6 w-full py-3">
						Search Business
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen px-4 py-6 sm:px-6 lg:px-8" style={{ background: theme.background }}>
			<div className="mx-auto max-w-5xl">
				<div className="rounded-2xl border p-5 sm:p-8" style={{ borderColor: subtleBorder, background: theme.card }}>
					<div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
						<div>
							<img
								src={splashIcon}
								alt="ToronQ logo"
								className="h-14 w-auto object-contain sm:h-16"
							/>

							<p className="mt-5 text-sm font-semibold" style={{ color: theme.primary }}>
								Your Reputation Pulse
							</p>

							{businessData?.name && (
								<p className="mt-3 text-base font-semibold" style={{ color: theme.primary }}>
									{businessData.name}
								</p>
							)}

							<h1 className="mt-3 text-2xl font-semibold leading-tight sm:text-3xl" style={{ color: theme.text }}>
								See how your business looks online.
							</h1>

							<div className="mt-6 grid gap-4">
								<StatCard
									icon={<FaCheckCircle />}
									label="Total Reviews"
									value={stats.totalReviews}
									iconColor={theme.success}
								/>

								<StatCard
									icon={<FaExclamationTriangle />}
									label="Unanswered"
									value={stats.unansweredReviews}
									iconColor={theme.danger}
								/>

								<StatCard
									icon={<FaChartLine />}
									label="Average Rating"
									value={`${stats.averageRating}/5`}
									iconColor={theme.warning}
								/>
							</div>
						</div>

						<div className="rounded-2xl border p-6" style={{ borderColor: subtleBorder, background: theme.secondary }}>
							<div className="rounded-2xl p-5" style={{ background: `${scoreColor}12` }}>
								<div className="flex items-center justify-between">
									<p className="text-sm font-medium" style={{ color: theme.textMuted }}>
										Reputation Score
									</p>

									<div
										className="rounded-full px-3 py-1 text-xs font-semibold"
										style={{
											backgroundColor: `${scoreColor}22`,
											color: scoreColor,
										}}
									>
										{scoreLabel}
									</div>
								</div>

								<div className="mt-6 flex justify-center">
									<div className="relative h-[220px] w-[220px]">
										<svg
											viewBox="0 0 180 180"
											className="-rotate-90 h-full w-full"
										>
											<circle
												cx="90"
												cy="90"
												r={normalizedRadius}
												stroke={mode === "dark" ? "rgba(255,255,255,0.1)" : "#E7EDF8"}
												strokeWidth={stroke}
												fill="transparent"
											/>

											<circle
												cx="90"
												cy="90"
												r={normalizedRadius}
												stroke={scoreColor}
												strokeWidth={stroke}
												fill="transparent"
												strokeLinecap="round"
												strokeDasharray={circumference}
												strokeDashoffset={strokeDashoffset}
											/>
										</svg>

										<div className="absolute inset-0 flex flex-col items-center justify-center">
											<p className="text-6xl font-semibold" style={{ color: theme.text }}>
												{displayScore}
											</p>
											<p className="text-sm" style={{ color: theme.textLight }}>/100</p>
										</div>
									</div>
								</div>

								<div className="mt-4 text-center">
									<h2 className="text-2xl font-semibold" style={{ color: scoreColor }}>
										{scoreLabel}
									</h2>
								</div>
							</div>

							<div className="mt-6 rounded-2xl border p-4" style={{ borderColor: subtleBorder, background: theme.card }}>
								<p className="text-sm font-semibold" style={{ color: theme.text }}>
									Quick Insight
								</p>

								<p className="mt-2 text-sm leading-6" style={{ color: theme.textMuted }}>
									Replying to unanswered reviews can improve customer confidence
									and strengthen your local search presence.
								</p>
							</div>

							<div className="mt-6">
								<Button
									variant="primary"
									theme={theme}
									onClick={() => navigate("/dashboard/reviews")}
									className="w-full py-4 text-base"
									style={{ background: theme.success }}
								>
									Start Replying, It's Free
									<FaArrowRight />
								</Button>

								<p className="mt-3 text-center text-sm" style={{ color: theme.textMuted }}>
									Your first 3 AI replies are on us
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
